import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Switch, useParams, useHistory } from "react-router-dom";

import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Box from "@material-ui/core/Box";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import MaterialAlert from "@material-ui/lab/Alert";
import MaterialAlertTitle from "@material-ui/lab/AlertTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import Alert from "../components/common/Alert";
import Copyright from "../components/common/Copyright";
import { uiActions } from "../store/ui-slice";
import { registerCustomer } from "../store/customers-actions";
import { validateEmail } from "../helpers/validations";
import { getRequestConfirmUser } from "../services/users-requests";
import useHttp from "../hooks/use-http";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    "& > * + *": {
      marginTop: theme.spacing(2),
    },
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

const RegistrationForm = () => {
  const classes = useStyles();
  const history = useHistory();

  const emailInputRef = useRef();
  const companyInputRef = useRef();
  const passwordInputRef = useRef();
  const retypedPasswordInputRef = useRef();

  const dispatch = useDispatch();
  const notification = useSelector((state) => state.ui.notification);

  const loginButtonHandler = (event) => {
    event.preventDefault();
    if (!validateEmail(emailInputRef.current.value)) {
      dispatch(
        uiActions.enqueNotification({
          message: "Invalid email address",
          variant: "error",
        })
      );
      return;
    }
    if (
      passwordInputRef.current.value !== retypedPasswordInputRef.current.value
    ) {
      dispatch(
        uiActions.enqueNotification({
          message: "Invalid password or not identical",
          variant: "error",
        })
      );
      return;
    }
    dispatch(
      registerCustomer({
        email: emailInputRef.current.value,
        name: emailInputRef.current.value,
        company: emailInputRef.current.value,
        password: passwordInputRef.current.value,
      })
    );
  };

  const loginLinkClickHandler = () => {
    history.push("/");
    dispatch(uiActions.clearNotification());
  };

  return (
    <>
      <Typography component="h1" variant="h5">
        Sign up
      </Typography>
      <form className={classes.form} noValidate>
        <TextField
          inputRef={emailInputRef}
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email"
          name="email"
          autoComplete="email"
          autoFocus
        />
        <TextField
          inputRef={passwordInputRef}
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="current-password"
        />
        <TextField
          inputRef={retypedPasswordInputRef}
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="retyped-password"
          label="Retype password"
          type="password"
          id="retyped-password"
        />
        {notification && (
          <Alert severity={notification.variant}>{notification.message}</Alert>
        )}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          onClick={loginButtonHandler}
          className={classes.submit}
        >
          Sign Up
        </Button>
      </form>
      <Link component="button" variant="body2" onClick={loginLinkClickHandler}>
        Already hava an account? Sign in
      </Link>
    </>
  );
};

const ActivationConfirmed = () => {
  const params = useParams();
  const classes = useStyles();
  const history = useHistory();
  const { isLoading, error, sendRequest } = useHttp();

  useEffect(() => {
    sendRequest(getRequestConfirmUser(params.token), false, () => {
    });
  }, [sendRequest, params.token]);

  const content = error ? (
    <>
      <MaterialAlert severity="error">
        <Typography variant="h5" gutterBottom>
          Your account was not activated.
        </Typography>
        <Typography variant="body1" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Please contact system administrator.</strong>
        </Typography>
      </MaterialAlert>
    </>
  ) : (
    <>
      <Typography component="h1" variant="h5">
        Account activated
      </Typography>
      <MaterialAlert severity="success">
        <MaterialAlertTitle>Success</MaterialAlertTitle>
        Your account is now activated.{" "}
        <strong>Click the button below to login.</strong>
      </MaterialAlert>

      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={() => {
          history.replace("/");
        }}
        className={classes.submit}
      >
        Login
      </Button>
    </>
  );

  return (
    <>
      {!isLoading && content}
      {isLoading && (
        <>
          <Typography component="h1" variant="h5">
            Activating account
          </Typography>
          <CircularProgress />
        </>
      )}
    </>
  );
};

export default function SignUp() {
  const classes = useStyles();

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockOutlinedIcon />
        </Avatar>
        <Switch>
          <Route path="/signup/:token">
            <ActivationConfirmed />
          </Route>
          <Route path="/signup">
            <RegistrationForm />
          </Route>
        </Switch>
      </div>
      <Box mt={8}>
        <Copyright />
      </Box>
    </Container>
  );
}
